import { useMemo, useCallback } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

import {
  accountsEndpoints as endpoints,
  permissionsEndpoints,
  membersEndpoints,
  userEndpoints,
} from '@/api/endpoints';
import { callDelete, callGet, callPost, callPut } from '@/api/fetcher';
import { popModal } from '@/redux/modals/actions';
import { setPermissions } from '@/redux/permissions/actions';
import { selectAllAgents } from '@/redux/presence/selectors';
import { setAccountSlug } from '@/redux/session/actions';
import { selectAccountSlug } from '@/redux/session/selectors';
import { selectIsAuthenticated } from '@/redux/user/selectors';
import { trackEvent } from '@/segment/segment';
import { Account, Accounts, Agents } from '@/shared/types/account';
import { RoleType } from '@/shared/types/member';
import { EventName } from '@/shared/types/segment';
import { User } from '@/shared/types/user';

import useLocalStorage from './useLocalStorage';
import useUser from './useUser';

const API = {
  listAccounts: async (): Promise<Accounts> => callGet(endpoints.accounts),
  getAccount: async (): Promise<Account> => callGet(endpoints.account),
  createAccount: async (newAccount: Partial<Account>): Promise<Account> =>
    callPost(endpoints.accounts, newAccount),
  updateAccount: async (updates: Partial<Account>): Promise<Account> =>
    callPut(endpoints.account, updates),
  deleteAccount: async (): Promise<Account> => callDelete(endpoints.account),
  leaveAccount: async (): Promise<{ account_id: string; user_id: string }> =>
    callPost(endpoints.leave, {}),
  checkSlug: async (id: string): Promise<boolean> => {
    try {
      await callGet(endpoints.checkSlug(id));
      return true;
    } catch {
      return false;
    }
  },
  listAgents: async (): Promise<Agents> => callGet(endpoints.agents),
  transferOwnership: async (data: { new_owner_id: string }): Promise<Account> =>
    callPut(endpoints.transfer, data),
};

export const useAccount = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useUser();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const accountSlug = useSelector(selectAccountSlug);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const presenceAgents = useSelector(selectAllAgents);
  const [lastUsedAccountSlug, setLastUsedAccountSlug] = useLocalStorage(
    'lastUsedAccountSlug',
    null
  );

  const { data: accounts, status: accountsStatus } = useQuery<Accounts, Error>({
    queryKey: [endpoints.accounts, user?.user_id],
    queryFn: API.listAccounts,
    enabled: isAuthenticated,
  });

  const sortedAccounts = useMemo(
    () =>
      accounts?.accounts?.sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1)),
    [accounts?.accounts]
  );

  const { data: account, status: accountStatus } = useQuery<Account, Error>({
    queryKey: [endpoints.account, accountSlug],
    queryFn: API.getAccount,
    enabled: isAuthenticated && !!accountSlug && !!user,
  });

  const {
    mutate: updateAccount,
    mutateAsync: updateAccountAsync,
    status: updateStatus,
  } = useMutation<Account, Error, Partial<Account>>({
    mutationFn: API.updateAccount,
    onSuccess: (resp) => {
      queryClient.setQueryData<Account>([endpoints.account, accountSlug], (prev: Account) => ({
        ...prev,
        ...resp,
      }));

      queryClient.setQueryData<Accounts>([endpoints.accounts, user?.user_id], (prev: Accounts) => ({
        accounts: prev?.accounts.map((item) =>
          item.account_id === resp.account_id ? { ...item, ...resp } : item
        ),
      }));

      queryClient.setQueryData<User>([userEndpoints.user], (prev) =>
        prev?.accounts
          ? {
              ...prev,
              accounts: prev.accounts.map((item) =>
                item.account_id === resp.account_id ? { ...item, ...resp } : item
              ),
            }
          : prev
      );
    },
    meta: { errorToast: true },
  });

  const { mutate: createAccount, status: createStatus } = useMutation<
    Account,
    Error,
    Partial<Account>
  >({
    mutationFn: API.createAccount,
    onSuccess: (resp) => {
      queryClient.setQueryData<Accounts>([endpoints.accounts, user?.user_id], (prev) => ({
        accounts: [...(prev?.accounts || []), resp],
      }));
      toast.success(t('accounts.created'));
    },
    meta: { errorToast: true },
  });

  const { mutate: transferOwnership, status: transferOwnershipStatus } = useMutation<
    Account,
    Error,
    { new_owner_id: string }
  >({
    mutationFn: API.transferOwnership,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [endpoints.account, accountSlug] });
      queryClient.invalidateQueries({ queryKey: [membersEndpoints.members, accountSlug] });
      queryClient.invalidateQueries({ queryKey: [endpoints.accounts, user?.user_id] });
      queryClient.invalidateQueries({
        queryKey: [permissionsEndpoints.permissions, account?.account_id],
      });
    },
  });

  const { mutate: deleteAccount, status: deleteStatus } = useMutation<Account, Error>({
    mutationFn: API.deleteAccount,
    onSuccess: () => {
      dispatch(popModal());
      queryClient.removeQueries({ queryKey: [endpoints.account, accountSlug] });
      const newAccounts = queryClient.setQueryData<Accounts>(
        [endpoints.accounts, user?.user_id],
        (prev: Accounts) => {
          const accountsCopyArray = prev
            ? prev?.accounts.filter((acc) => acc.slug !== accountSlug)
            : [];

          return { accounts: accountsCopyArray };
        }
      );

      // Check if there are any accounts remaining
      if (newAccounts.accounts.length > 0) {
        navigate(`/${newAccounts.accounts[0].slug}/`);
        dispatch(setAccountSlug(newAccounts.accounts[0].slug));
      } else {
        // No accounts remaining, clear the slug and redirect to account creation
        dispatch(setAccountSlug(null));
        navigate('/create-account');
      }

      toast.success(t('accounts.deleted'));
    },
    meta: { errorToast: true },
  });

  const { mutate: leaveAccount, status: leaveStatus } = useMutation<
    { account_id: string; user_id: string },
    Error
  >({
    mutationFn: API.leaveAccount,
    onSuccess: () => {
      dispatch(popModal());
      queryClient.removeQueries({ queryKey: [endpoints.account, accountSlug] });
      const newAccounts = queryClient.setQueryData<Accounts>(
        [endpoints.accounts, user?.user_id],
        (prev: Accounts) => {
          const accountsCopyArray = prev
            ? prev?.accounts.filter((acc) => acc.slug !== accountSlug)
            : [];

          return { accounts: accountsCopyArray };
        }
      );

      // Check if there are any accounts remaining
      if (newAccounts.accounts.length > 0) {
        navigate(`/${newAccounts.accounts[0].slug}/`);
        dispatch(setAccountSlug(newAccounts.accounts[0].slug));
      } else {
        // No accounts remaining, clear the slug and redirect to account creation
        dispatch(setAccountSlug(null));
        navigate('/create-account');
      }

      toast.success(t('accounts.left'));
    },
    meta: { errorToast: true },
  });

  const { mutateAsync: checkSlugAsync } = useMutation<boolean, Error, string>({
    mutationFn: API.checkSlug,
  });

  // find the role of the user
  const member = useMemo(
    () => account?.members?.find((m) => m.user_id === user?.user_id),
    [account?.members, user?.user_id]
  );

  // Validates if the account slug belongs to the user
  const isValidSlug = useCallback(() => {
    const availableSlugs = user?.accounts?.map((account) => account.slug);
    return availableSlugs?.includes(accountSlug);
  }, [user?.accounts, accountSlug]);

  const switchAccount = useCallback(
    (newSlug: string) => {
      // Here, not at the call sites: the menu and the palette both switch.
      trackEvent(EventName.SelectAccount);
      // Queries only. Clearing the mutation cache cancels nothing, and a mutation paused at that
      // moment becomes invisible to `resumePausedMutations` — its promise never settles.
      queryClient.getQueryCache().clear();
      setLastUsedAccountSlug(newSlug);
      dispatch(setAccountSlug(newSlug));
      dispatch(setPermissions(null));
    },
    [queryClient, setLastUsedAccountSlug, dispatch]
  );

  const { data: agentsIds } = useQuery<Agents, Error>({
    queryKey: [endpoints.agents, accountSlug],
    queryFn: API.listAgents,
    enabled: isAuthenticated && !!accountSlug && !!user,
  });

  const agents = useMemo(
    () => presenceAgents?.filter((agent) => agentsIds?.agent_ids.includes(agent.agent_id)) ?? [],
    [agentsIds?.agent_ids, presenceAgents]
  );

  const agentsExceptUser = useMemo(() => {
    return agents?.filter((agent) => agent.agent_id !== user?.user_id);
  }, [agents, user?.user_id]);

  const computedLastUsedAccountSlug = useMemo(
    () => accounts?.accounts?.find((acc) => acc.slug === lastUsedAccountSlug)?.slug,
    [accounts?.accounts, lastUsedAccountSlug]
  );
  const accountRole = useMemo(() => member?.role as RoleType, [member?.role]);
  const accountId = useMemo(() => account?.account_id, [account?.account_id]);

  return {
    accounts: sortedAccounts,
    accountsStatus,
    accountId,
    slug: accountSlug,
    account,
    accountStatus,
    accountRole,
    createAccount,
    createStatus,
    deleteAccount,
    deleteStatus,
    leaveAccount,
    leaveStatus,
    updateAccount,
    updateAccountAsync,
    updateStatus,
    checkSlugAsync,
    transferOwnership,
    transferOwnershipStatus,
    lastUsedAccountSlug: computedLastUsedAccountSlug,
    setLastUsedAccountSlug,
    isValidSlug,
    switchAccount,
    agents,
    agentsExceptUser,
  };
};
